import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { CourseVideoView } from '../components/courses/CourseVideoView';
import { CourseDetailView } from '../components/courses/CourseDetailView';
import { CourseListView } from '../components/courses/CourseListView';

export default function MyCoursesPage() {
  const { courseId } = useParams();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await api.get('/api/my-courses');
      setCourses(response.data);
    } catch (error) {
      // Course fetch failed silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    if (courseId) {
      const fetchLessons = async () => {
        try {
          const [courseRes, lessonsRes] = await Promise.all([
            api.get(`/api/courses/${courseId}`),
            api.get(`/api/courses/${courseId}/lessons`)
          ]);
          setSelectedCourse(courseRes.data);
          setLessons(lessonsRes.data);
          const firstIncomplete = lessonsRes.data.find(l => !l.completed) || lessonsRes.data[0];
          setActiveLesson(firstIncomplete);
        } catch (error) {
          // Course details fetch failed silently
        }
      };
      fetchLessons();
    } else {
      setSelectedCourse(null);
      setLessons([]);
      setActiveLesson(null);
    }
  }, [courseId]);

  const updateProgress = async (lessonId, videoPosition, completed = false) => {
    try {
      await api.post('/api/progress/update', {
        course_id: courseId, lesson_id: lessonId, video_position: videoPosition, completed
      });
      if (completed) {
        setLessons(prev => prev.map(l => 
          l.id === lessonId ? { ...l, completed: true } : l
        ));
      }
    } catch (error) {
      // Progress update failed silently
    }
  };

  const handleMarkComplete = async () => {
    if (activeLesson) {
      await updateProgress(activeLesson.id, 0, true);
    }
  };

  const handleVideoTimeUpdate = (e) => {
    if (activeLesson) {
      const position = e.target.currentTime;
      if (Math.floor(position) % 10 === 0) {
        updateProgress(activeLesson.id, position, false);
      }
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton h-32 rounded-md"></div>
          ))}
        </div>
      </div>
    );
  }

  if (selectedCourse && activeLesson) {
    return (
      <CourseVideoView
        selectedCourse={selectedCourse}
        lessons={lessons}
        activeLesson={activeLesson}
        setActiveLesson={setActiveLesson}
        videoRef={videoRef}
        handleVideoTimeUpdate={handleVideoTimeUpdate}
        handleMarkComplete={handleMarkComplete}
      />
    );
  }

  if (selectedCourse) {
    return (
      <CourseDetailView
        selectedCourse={selectedCourse}
        lessons={lessons}
        setActiveLesson={setActiveLesson}
      />
    );
  }

  return <CourseListView courses={courses} />;
}
